import { IClient, Typestate } from "@interfaces";
import { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, Select, DatePicker } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";
import { enumReferred, region_towship, gender, hospitalNames, insulinTypes, PATH_CDIC_CLIENT_INFO, regionTownships, enumJoinedProgram } from "@configs";
import { setLoading } from "@redux";
import {
  getApiErrorMessage,
  useCreateClient,
  useNotify,
  useUpdateClient,
} from "@utils";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import dayjs, { Dayjs } from "dayjs";
import { SharedButton } from "@components";
import styled from "styled-components";

interface IProps {
  client?: IClient;
}
const dateFormat = "DD-MM-YYYY";
const initValue: IClient = {
  name: "",
  gender: "",
  phoneNumber: "",
  state: "",
  township: "",
  hospitalName: "",
  dateOfBirth: dayjs().subtract(18, "year"),
  age: dayjs().subtract(18, "year").year(),
  cgCount: 0,
  crCount: 0,
  noOfChildren: 0,
  geastralMonth: 0,
  pragrancyStatus: true,
  referredBy: [],
  deliverySite: "",
  ANCareVisitStatus: false,
  status: false,
  stateId: "",
  insulinType: "",
  remark: "",
};

export const CdicClientForm = memo(({ client }: IProps) => {
  const { YupCdicClient } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const clientSchema = Yup.object().shape(YupCdicClient);
  const [data, setData] = useState<IClient>(initValue);
  const [region, setRegion] = useState<string>("");
  const [clientForm] = Form.useForm();
  const [townships, setTownships] = useState<string[]>([]);
  const isSubmitting = createClient.isPending || updateClient.isPending;

  useEffect(() => {
    dispatch(setLoading(isSubmitting));
  }, [dispatch, isSubmitting]);

  useEffect(() => {
    clientForm.setFieldsValue({ program: enumJoinedProgram.CDIC });
  }, [clientForm, data]);
  useEffect(() => {
    if (client) {
      setData({ ...data, ...client, dateOfBirth: dayjs(client.dateOfBirth) });
      clientForm.setFieldsValue({
        ...data,
        ...client,
        noOfChildren: Number(client?.noOfChildren),
        dateOfBirth: dayjs(client.dateOfBirth),
        age: dayjs(client?.dateOfBirth).year().toString(),
      });
      setRegion(client?.state || "");
    }
  }, [client]);

  const onFinish = async (values: IClient) => {
    try {
      if (client) {
        await updateClient.mutateAsync({
          payload: {
            ...data,
            ...values,
            noOfChildren: Number(values?.noOfChildren),
            dateOfBirth: dayjs(values.dateOfBirth),
            age: dayjs(client?.dateOfBirth).year().toString(),
            status: client?.status,
            deliverySite: client?.deliverySite,
          } as IClient,
          id: data?.userId || "",
        });
        success(t("message.update.success"));
      } else {
        await createClient.mutateAsync({
          ...data,
          ...values,
          noOfChildren: Number(values?.noOfChildren),
          dateOfBirth: dayjs(values.dateOfBirth),
          age: dayjs(values?.dateOfBirth).year().toString(),
          gestational_age_of_1_st_AN_care_visit: values?.gestational_age_of_1_st_AN_care_visit || "-",
        } as IClient);
        success(t("message.create.success"));
      }
      history.push(PATH_CDIC_CLIENT_INFO);
    } catch (err) {
      error(getApiErrorMessage(err, t("message.create.fail")));
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await clientSchema.validateSyncAt(field, { [field]: value });
    },
  };
  const selectedState: Typestate = Form.useWatch("state", clientForm);
  const selectedTownship: string = Form.useWatch("township", clientForm);
  const dateOfBirth: Dayjs | string = Form.useWatch("dateOfBirth", clientForm);
  const referredBy: enumReferred[] = Form.useWatch("referredBy", clientForm);
  
  useEffect(() => {
    if (selectedState) {
      const townships = regionTownships[selectedState.toLowerCase()];
      const oldTownship = region_towship[selectedState.toLowerCase()];
      setTownships(townships || oldTownship);
    }
  }, [selectedState]);
  useEffect(() => {
    if (dateOfBirth && dayjs.isDayjs(dateOfBirth)) {
      const calculatedAge = dayjs().diff(dateOfBirth, 'year');
      clientForm.setFieldsValue({ 
        age: calculatedAge 
      });
    }
  }, [dateOfBirth, clientForm]);

  const handleChangeState = (value: string) => {
    clientForm.setFieldsValue({ township: "" });
  };

  const goBack = () => history.goBack();
  return (
    <Form
      form={clientForm}
      name="basic"
      initialValues={data}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.name")} name="name" rules={[yupSync]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.phone_number_require")} name="phoneNumber" rules={[yupSync]}>
            <Input type="number" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.date_of_birth")} name="dateOfBirth" rules={[yupSync]}>
            <DatePicker format={dateFormat} />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.gender")} name="gender" rules={[yupSync]}>
            <Select>
              {gender.map((item) => (
                <Select.Option value={item} key={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.state")} name="state" rules={[yupSync]}>
            <Select value={selectedState} onChange={handleChangeState}>
              {Object.keys(regionTownships).map((item) => (
                <Select.Option value={item} key={item}>
                  {t(`page.${item}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.townshship")} name="township" rules={[yupSync]}>
            <Select value={selectedTownship}>
              {selectedState &&
                townships.map((item) => (
                  <Select.Option value={item} key={item}>
                    {item}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.hospital_name")} name="hospitalName" rules={[yupSync]}>
            <Select>
              {hospitalNames.map((item) => (
                <Select.Option value={item} key={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.insulin_type")} name="insulinType" rules={[yupSync]}>
            <Select>
              {insulinTypes.map((item) => (
                <Select.Option value={item} key={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      {/* <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item label={t("page.referredBy")} name="referredBy" rules={[yupSync]}>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="choose referred by"
              onChange={handleChangeReffer}
              value={referredBy}
              options={options}
              showSearch={false}
            />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.anc_visit_status")}
            name="ANCareVisitStatus"
            valuePropName="value"
            // define valuePropName is what value will accept by radio group
          >
            <Radio.Group>
              <Space>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row> */}
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.age")}
            name="age"
          >
            <Input type="number" />
            {/* <Select placeholder={t("page.select")}>
              {gestationalAgeOptions.map((item) => (
                <Select.Option key={item} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select> */}
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.remark")}
            name="remark"
          >
            <Input.TextArea rows={4} placeholder={t("page.remark")} />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item name="program" hidden>
            <Input type="hidden" />
          </Form.Item>
        </Col>
      </Row>
      {/* <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}>
          <Form.Item
            label={t("page.total_numbers_of_AN_care_visit")}
            name="total_numbers_of_AN_care_visit"
            rules={[yupSync]}
          >
            <Input type="number" />
          </Form.Item>
        </Col>
        <Col lg={12} md={24} sm={24} xs={24}>
        </Col>
      </Row> */}

      <Form.Item>
        <StyledFormAction>
          <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
            {t("page.cancel")}
          </SharedButton>
          <SharedButton type="primary" htmlType="submit" size="middle">
            {t("page.create")}
          </SharedButton>
        </StyledFormAction>
      </Form.Item>
    </Form>
  );
});
const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
